import { useFetchCase } from "@/queries/useFetchCase.ts";
import { useFetchCompanionCasesForAutocomplete } from "@/queries/useFetchCompanionCasesForAutocomplete.ts";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

type Option = {
    id: string;
    name: string;
};

type Props = {
    foundCaseId?: string;
};
const AssignCaseAutocomplete = ({ foundCaseId }: Props) => {
    const { setValue, formState, watch } = useFormContext();
    const [searchString, setSearchString] = useState("");
    const [inputValue, setInputValue] = useState("");
    const hasSetInitialValue = useRef(false);

    const {
        data: { data: caseData } = {},
    } = useFetchCase(foundCaseId);

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchCompanionCasesForAutocomplete({
        searchString: searchString,
    });

    const options: Option[] = useMemo(() => {
        let newOptions: Option[] = [];

        if (data) {
            newOptions = data.map((caseDocument) => ({
                id: caseDocument.idUUID,
                name: `#${caseDocument.id} ${caseDocument.clientName}`,
            }));
        }

        if (caseData) {
            newOptions = newOptions.filter((option) => option.id !== caseData.idUUID);

            const newOption = {
                id: caseData.idUUID,
                name: `#${caseData.id} ${caseData.clientName}`,
            };
            newOptions.push(newOption);
        }

        return newOptions;
    }, [data, caseData]);

    // Set initial value when foundCaseId data loads (only once)
    useEffect(() => {
        if (caseData && !hasSetInitialValue.current) {
            const newOption = {
                id: caseData.idUUID,
                name: `#${caseData.id} ${caseData.clientName}`,
            };
            setValue("case", newOption);
            setInputValue(`#${caseData.id} ${caseData.clientName}`);
            hasSetInitialValue.current = true;
        }
    }, [caseData, setValue]);

    return (
        <>
            {isError && <Typography variant={"h5"}>Error loading cases.</Typography>}
            <Autocomplete
                value={watch("case")}
                inputValue={inputValue}
                onInputChange={(_event, newInputValue) => {
                    setInputValue(newInputValue);

                    if (newInputValue.length >= 2) {
                        setSearchString(newInputValue);
                    } else {
                        setSearchString("");
                    }
                }}
                renderInput={(params) => <TextField {...params} label="Client name or Case ID" />}
                options={options}
                loading={isLoading || isRefetching}
                getOptionLabel={(option: Option) => option.name ?? ""}
                onChange={(_event, value) => {
                    setValue("case", value);
                    setInputValue(value ? value.name : "");
                    setSearchString("");
                }}
                autoHighlight
                blurOnSelect
                sx={{
                    "& .MuiAutocomplete-inputRoot": {
                        padding: "4px!important",
                    },
                    "& .MuiAutocomplete-input": {
                        paddingTop: "4px!important",
                        paddingBottom: "4px!important",
                    },
                }}
            />
            {formState.errors.case !== undefined && (
                <Box sx={{ color: "red", fontSize: "12px", ml: 1, mt: 1 }}>
                    You must select a case.
                </Box>
            )}
        </>
    );
};

export default AssignCaseAutocomplete;
